import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Siren, Users, CheckCircle2 } from "lucide-react";

const card = "border border-[#2C2D35] bg-[#141518] rounded-sm p-3";

// Field-Supervisor Command Center (Add-ons 23-25, 28) — lives on the Supervisor page.
export default function SupervisorCommand() {
  const [d, setD] = useState(null);
  const load = () => client.get("/supervisor/overview").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, []);
  const ackSafety = async (r, resolve) => {
    let reason = "";
    if (resolve) { reason = window.prompt("Documented resolution reason (required):") || ""; if (!reason.trim()) return; }
    try { await client.post(`/safety-reports/${r.id}/ack`, { resolve, reason }); toast.success(resolve ? "Resolved — audited" : "Acknowledged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const correct = async (u) => {
    const s = window.prompt(`Correct status for ${u.unit_id} (current: ${u.status}). New status:`, "available");
    if (!s?.trim()) return;
    const reason = window.prompt("Operational reason (required, audited with previous value):");
    if (!reason?.trim()) return;
    try { await client.post("/supervisor/unit-action", { unit: u.unit_id, status: s.trim(), reason: reason.trim() }); toast.success("Corrected — original preserved in audit"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const priority = async (i) => {
    const p = window.prompt(`New priority for ${i.cc_number} (current P${i.priority}):`, String(i.priority));
    if (!p?.trim()) return;
    const reason = window.prompt("Reason (required):");
    if (!reason?.trim()) return;
    try { await client.post(`/incidents/${i.id}/priority`, { priority: Number(p), reason: reason.trim() }); toast.success("Priority changed — audited"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!d) return null;
  const stats = [["P1 OPEN", d.p1_open, "#EF4444"], ["P2 OPEN", d.p2_open, "#F59E0B"],
    ["BACKUP REQS", d.backup_requests.length, "#F59E0B"], ["UNACKED", d.unacked_len ?? d.unacknowledged.length, "#F59E0B"],
    ["OOS UNITS", d.oos_units.length, "#9CA3AF"], ["PENDING RPTS", d.pending_reports, "#22D3EE"], ["RETURNED RPTS", d.returned_reports, "#A855F7"]];
  return (
    <div className="space-y-3 mb-4" data-testid="supervisor-command">
      {(d.emergency_activations || []).length > 0 && (
        <div className="border border-[#EF4444] bg-[#EF4444]/10 rounded-sm p-3" data-testid="sup-emergency-feed">
          <div className="font-head text-[11px] font-bold text-[#EF4444] mb-2 animate-pulse"><Siren size={12} className="inline mr-1" />EMERGENCY / SAFETY ACTIVATIONS — supervisor acknowledgment required</div>
          {d.emergency_activations.map((r) => (
            <div key={r.id} className="flex items-center gap-2 flex-wrap font-mono text-[11px] text-white py-1 border-b border-[#EF4444]/20">
              <b className="text-[#EF4444]">{r.label}</b><span>{r.unit}</span><span className="text-[#9CA3AF]">{r.by} · {r.ts?.slice(11, 19)}</span>
              {r.lat && <span className="text-[#22D3EE]">{r.lat.toFixed(4)},{r.lng.toFixed(4)}</span>}
              {r.note && <span>{r.note}</span>}
              {r.acknowledged_by ? <span className="text-[#10B981] text-[9px]">ACK {r.acknowledged_by}</span>
                : <button data-testid={`sup-ack-${r.id}`} onClick={() => ackSafety(r, false)} className="px-2 py-0.5 text-[9px] font-head font-bold bg-[#F59E0B] text-black rounded-sm">ACKNOWLEDGE</button>}
              <button data-testid={`sup-resolve-${r.id}`} onClick={() => ackSafety(r, true)} className="ml-auto px-2 py-0.5 text-[9px] font-head font-bold border border-[#10B981] text-[#10B981] rounded-sm"><CheckCircle2 size={9} className="inline" /> RESOLVE (reason req.)</button>
            </div>))}
        </div>)}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        {stats.map(([l, v, c]) => (
          <div key={l} className={card}><div className="font-mono text-[8px] text-[#9CA3AF]">{l}</div>
            <div className="font-head text-xl font-bold" style={{ color: v > 0 ? c : "#fff" }}>{v}</div></div>))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className={card} data-testid="sup-units-board">
          <div className="font-head text-[10px] font-bold text-[#22D3EE] mb-1.5"><Users size={11} className="inline mr-1" />UNIT BOARD — click CORRECT for audited status correction</div>
          <div className="max-h-56 overflow-y-auto">
            {(d.units || []).map((u) => (
              <div key={u.unit_id} className="flex items-center gap-2 font-mono text-[10px] text-[#C9CDD4] py-0.5 border-b border-[#2C2D35]/50">
                <b className="w-16 text-white">{u.unit_id}</b>
                <span style={{ color: u.status === "available" ? "#10B981" : u.status === "out_of_service" ? "#EF4444" : "#F59E0B" }}>{(u.status || "").replace(/_/g, " ").toUpperCase()}</span>
                {u.current_incident && <span className="text-[#22D3EE]">{u.current_incident}</span>}
                <span className="text-[#6B7280]">{u.status_since?.slice(11, 16)}</span>
                <button data-testid={`sup-correct-${u.unit_id}`} onClick={() => correct(u)} className="ml-auto px-1.5 py-0.5 text-[8px] font-head font-bold border border-[#22D3EE] text-[#22D3EE] rounded-sm">CORRECT</button>
              </div>))}
          </div>
        </div>
        <div className={card} data-testid="sup-incidents-board">
          <div className="font-head text-[10px] font-bold text-[#22D3EE] mb-1.5"><ShieldAlert size={11} className="inline mr-1" />ACTIVE INCIDENTS — audited priority control</div>
          <div className="max-h-56 overflow-y-auto">
            {(d.active_incidents || []).map((i) => (
              <div key={i.cc_number} className="flex items-center gap-2 font-mono text-[10px] text-[#C9CDD4] py-0.5 border-b border-[#2C2D35]/50">
                <b style={{ color: i.priority === 1 ? "#EF4444" : "#F59E0B" }}>P{i.priority}</b>
                <span className="text-white">{i.cc_number}</span>
                <span className="truncate max-w-[150px]">{i.call_type_label}</span>
                <span className="text-[#6B7280]">{(i.assigned_units || []).join(",") || "UNASSIGNED"}</span>
                <button data-testid={`sup-priority-${i.cc_number}`} onClick={() => priority(i)} className="ml-auto px-1.5 py-0.5 text-[8px] font-head font-bold border border-[#F59E0B] text-[#F59E0B] rounded-sm">PRIORITY</button>
              </div>))}
          </div>
        </div>
      </div>
    </div>
  );
}
